<template>
  <div class="job-multi-file-upload-management d-flex flex-column">
    <v-dialog :value="associating" persistent max-width="400">
      <v-card>
        <v-card-text class="pa-6">
          {{ $t('ai_asset_association_loading') }}
          <v-progress-linear indeterminate color="primary" class="mt-4" />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-container fluid class="pa-6 pt-0 d-flex flex-column flex-grow-1">
      <v-row no-gutters class="upload-columns flex-grow-1">
        <!-- LEFT COLUMN: Holding area for files to be uploaded -->
        <v-col cols="12" md="5" class="pr-3 d-flex">
          <v-card
            outlined
            class="holding-card d-flex flex-column"
            :class="{ 'is-dragging-native': isDraggingNativeFile }"
            @dragover.prevent="onNativeDragOver"
            @dragleave.prevent="onNativeDragLeave"
            @drop.prevent="onNativeDrop"
          >
            <div class="card-header pa-4 d-flex align-center">
              <v-icon class="mr-2">mdi-cloud-upload-outline</v-icon>
              <div class="flex-grow-1">
                <div class="text-subtitle-1 font-weight-medium">{{ $t('files_from_this_upload') }}</div>
              </div>

              <div class="text-caption grey--text">{{ $t('drag_right_to_organize') }}</div>
            </div>

            <!-- Empty state: no files added yet, centered attach action -->
            <div
              v-if="!pendingFiles.length"
              class="empty-holding-state d-flex flex-column align-center justify-center flex-grow-1 pa-8 text-center"
            >
              <v-icon size="72" color="primary">mdi-cloud-upload-outline</v-icon>
              <div class="text-h6 font-weight-medium mt-4">{{ $t('empty_holding_state_title') }}</div>
              <div class="text-body-2 grey--text mt-2 empty-holding-state-hint">
                {{ $t('empty_holding_state_hint') }}
              </div>
              <div class="d-flex flex-wrap justify-center mt-6">
                <v-btn color="primary" @click="openAttachFileDialog">
                  <v-icon left>mdi-upload</v-icon>
                  {{ $t('attach_files') }}
                </v-btn>
              </div>
            </div>

            <div v-else class="pa-4 pt-0 holding-card-scroll">
              <div class="d-flex flex-wrap mb-3">
                <v-btn color="primary" @click="openAttachFileDialog">
                  <v-icon left>mdi-upload</v-icon>
                  {{ $t('attach_files') }}
                </v-btn>
                <v-btn
                  v-if="aiAssociationAvailable"
                  class="ml-2 associate-with-ai-button"
                  :loading="associating"
                  :disabled="loading || uploading || associating || !unassignedFiles.length"
                  @click="associateWithAi"
                >
                  <v-icon left>mdi-auto-fix</v-icon>
                  {{ $t('associate_with_ai') }}
                </v-btn>
              </div>

              <v-chip-group v-model="statusFilter" mandatory column class="mb-3">
                <v-chip small filter value="unassigned" :color="statusFilter === 'unassigned' ? 'primary' : undefined">
                  {{ $t('unassigned') }} ({{ unassignedFiles.length }})
                </v-chip>
                <v-chip small filter value="assigned" :color="statusFilter === 'assigned' ? 'primary' : undefined">
                  {{ $t('assigned') }} ({{ assignedFiles.length }})
                </v-chip>
                <v-chip small filter value="all" :color="statusFilter === 'all' ? 'primary' : undefined">
                  {{ $t('all') }} ({{ pendingFiles.length }})
                </v-chip>
              </v-chip-group>

              <v-divider class="mb-3"></v-divider>

              <div v-if="!filteredFiles.length" class="text-center py-8 grey--text">
                <v-icon large color="grey lighten-1">mdi-file-outline</v-icon>
                <div class="mt-2">{{ $t('no_files_added_yet') }}</div>
              </div>

              <div v-if="selectedFileUids.length" class="d-flex align-center mb-3 selection-toolbar">
                <span class="text-caption grey--text darken-1--text">
                  {{ $t('selected_count', { count: selectedFileUids.length }) }}
                </span>
                <v-spacer></v-spacer>
                <v-btn text x-small @click="clearSelection">
                  {{ $t('clear_selection') }}
                </v-btn>
              </div>

              <div class="file-card-list">
                <div
                  v-for="pendingFile in filteredFiles"
                  :key="pendingFile.uid"
                  class="file-card"
                  :class="{ 'file-card-assigned': !!pendingFile.formatInstanceId, 'file-card-dragging': draggingFileUids.includes(pendingFile.uid), 'file-card-selected': isFileSelected(pendingFile) }"
                  draggable="true"
                  @dragstart="onFileDragStart($event, pendingFile)"
                  @dragend="onFileDragEnd"
                >
                  <v-icon class="drag-handle" left>mdi-drag-vertical</v-icon>

                  <div class="file-card-info flex-grow-1">
                    <div class="text-body-2 font-weight-regular text-truncate" :title="pendingFile.name">
                      {{ pendingFile.name }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ formatFileSize(pendingFile.size) }} • {{ fileExtension(pendingFile) }}
                    </div>
                    <div v-if="!pendingFile.formatInstanceId" class="text-caption warning--text d-flex align-center">
                      <v-icon size="18" color="warning" class="mr-1">mdi-alert-circle</v-icon>
                      {{ $t('unassigned_drag_hint') }}
                    </div>
                    <div v-else class="text-caption success--text d-flex align-center">
                      <v-icon size="18" color="success" class="mr-1">mdi-check-circle</v-icon>
                      {{ getAssignedFormatLabel(pendingFile) }}
                    </div>
                  </div>

                  <div class="file-card-actions d-flex flex-column align-center">
                    <v-checkbox
                      :input-value="isFileSelected(pendingFile)"
                      class="file-card-checkbox mt-0 pt-0"
                      hide-details
                      dense
                      @click.stop="toggleFileSelection(pendingFile)"
                    ></v-checkbox>

                    <v-btn
                      icon
                      :color="pendingFile.formatInstanceId ? undefined : 'error'"
                      :title="pendingFile.formatInstanceId ? $t('unassign') : $t('remove')"
                      @click="handleFileAction(pendingFile)"
                    >
                      <v-icon>{{ pendingFile.formatInstanceId ? 'mdi-restore' : 'mdi-delete-outline' }}</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- RIGHT COLUMN: Deliverables and formats -->
        <v-col cols="12" md="7" class="pl-3 d-flex">
          <v-card outlined class="deliverables-card d-flex flex-column">
            <div class="card-header pa-4 pb-0 d-flex align-center">
              <v-icon class="mr-2">mdi-package-variant</v-icon>
              <div class="flex-grow-1">
                <span class="text-subtitle-1 font-weight-medium">{{ $t('deliverables_and_formats') }}</span>
                <span class="ml-2 text-caption grey--text">
                  {{ baseInstances.length }} {{ $t('deliverable_singular') }} •
                  {{ totalFormats }} {{ $t('formats') }}
                </span>
              </div>

              <v-btn small text @click="toggleAll">
                <v-icon left>{{ allDeliverablesExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
                {{ allDeliverablesExpanded ? $t('close_all') : $t('open_all') }}
              </v-btn>
            </div>

            <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

            <div class="pa-4 deliverables-card-scroll">
              <div v-if="!loading && baseInstances.length === 0" class="text-center py-12 grey--text">
                <v-icon large color="grey lighten-1">mdi-package-variant-closed</v-icon>
                <div class="mt-2">{{ $t('no_deliverables_added') }}</div>
              </div>

              <v-card
                v-for="(baseInstance, index) in baseInstances"
                :key="baseInstance.base_instance_id"
                class="deliverable-item mb-4"
                outlined
              >
                <div class="d-flex align-center pa-3 deliverable-header-row" @click="toggleDeliverable(baseInstance)" style="cursor: pointer;">
                  <v-icon class="mr-2">
                    {{ expandedDeliverables[baseInstance.base_instance_id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                  </v-icon>

                  <v-chip color="primary lighten-2" class="mr-3 font-weight-bold black--text" label small>
                    #{{ index + 1 }}
                  </v-chip>

                  <v-icon class="mr-2">{{ getBaseInstanceIcon(baseInstance) }}</v-icon>
                  <span class="font-weight-medium mr-2">{{ baseInstance.base_title }}</span>

                  <v-spacer></v-spacer>

                  <v-chip outlined small>
                    {{ (baseInstance.formatInstances || []).length }} {{ $t('formats') }}
                  </v-chip>
                </div>

                <v-expand-transition>
                  <div v-show="expandedDeliverables[baseInstance.base_instance_id]" class="px-4 pb-4">
                    <v-card
                      v-for="formatInstance in baseInstance.formatInstances"
                      :key="formatInstance.format_instance_id"
                      outlined
                      class="format-drop-card mb-3"
                      :class="{ 'format-drop-card-hover': dragOverFormatInstanceId === formatInstance.format_instance_id }"
                    >
                      <div class="d-flex align-center pa-3 format-header-row" @click="toggleFormat(formatInstance)" style="cursor: pointer;">
                        <v-icon class="mr-2">{{ isFormatExpanded(formatInstance) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                        <span class="text-body-2">
                          {{ formatInstance.format_desc }} • {{ getFormatFullName(baseInstance, formatInstance) }}
                        </span>
                      </div>

                      <div v-show="isFormatExpanded(formatInstance)" class="pa-4 pt-0">
                        <v-list dense class="existing-asset-list py-0">
                          <template v-for="asset in getFormatAssets(formatInstance)">
                            <v-list-item
                              :key="'asset-' + asset.job_deliver_asset_id"
                              class="existing-asset-row"
                              :class="{ 'existing-asset-row-hover': dragOverAssetId === asset.job_deliver_asset_id }"
                              @dragover.prevent="onAssetDragOver(asset)"
                              @dragleave.prevent="onAssetDragLeave"
                              @drop.prevent="onAssetDrop($event, formatInstance, asset)"
                            >
                              <v-list-item-content class="py-1">
                                <v-list-item-title class="text-body-2 font-weight-regular">{{ asset.name }}</v-list-item-title>
                                <v-list-item-subtitle class="text-caption font-weight-regular">
                                  {{ getAssetCurrentFileName(asset) }}
                                </v-list-item-subtitle>
                              </v-list-item-content>

                              <v-list-item-action class="d-flex flex-row align-center my-0">
                                <template v-if="getLatestPendingVersionForAsset(asset)">
                                  <v-chip x-small label color="warning lighten-3" class="black--text mr-2">{{ $t('markup.new_version') }}</v-chip>
                                  <v-chip x-small label color="warning lighten-3" class="black--text mr-2">v{{ getNextVersionForAsset(asset, getLatestPendingVersionForAsset(asset)) }}</v-chip>
                                  <v-btn icon x-small @click="unassignFile(getLatestPendingVersionForAsset(asset))">
                                    <v-icon small>mdi-close</v-icon>
                                  </v-btn>
                                </template>
                                <template v-else>
                                  <v-chip x-small label class="mr-2">{{ $t('already_there') }}</v-chip>
                                  <v-chip x-small label class="black--text">v{{ asset.currentVersion }}</v-chip>
                                </template>
                              </v-list-item-action>
                            </v-list-item>

                            <!-- Additional files queued as further versions of the same asset -->
                            <v-list-item
                              v-for="pendingFile in getExtraAssignedVersionFilesForAsset(asset)"
                              :key="pendingFile.uid"
                              class="existing-asset-row assigned-pending-row pl-8"
                            >
                              <v-list-item-content class="py-1">
                                <v-list-item-title class="text-body-2">{{ pendingFile.name }}</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">{{ formatFileSize(pendingFile.size) }}</v-list-item-subtitle>
                              </v-list-item-content>

                              <v-list-item-action class="d-flex flex-row align-center my-0">
                                <v-chip x-small label color="warning lighten-3" class="mr-2">{{ $t('markup.new_version') }}</v-chip>
                                <v-chip x-small label color="warning lighten-3" class="mr-2">v{{ getNextVersionForAsset(asset, pendingFile) }}</v-chip>
                                <v-btn icon x-small @click="unassignFile(pendingFile)">
                                  <v-icon small>mdi-close</v-icon>
                                </v-btn>
                              </v-list-item-action>
                            </v-list-item>
                          </template>

                          <v-list-item
                            v-for="pendingFile in getAssignedFilesForFormat(formatInstance)"
                            :key="pendingFile.uid"
                            class="existing-asset-row assigned-pending-row"
                          >
                            <v-list-item-content class="py-1">
                              <v-list-item-title class="text-body-2 font-weight-regular">{{ pendingFile.name }}</v-list-item-title>
                              <v-list-item-subtitle class="text-caption">{{ formatFileSize(pendingFile.size) }}</v-list-item-subtitle>
                            </v-list-item-content>

                            <v-list-item-action class="d-flex flex-row align-center my-0">
                              <v-chip x-small label color="warning lighten-3" class="black--text mr-2">{{ $t('new_file') }}</v-chip>
                              <v-chip x-small label color="warning lighten-3" class="black--text mr-2">v1</v-chip>
                              <v-btn icon x-small @click="unassignFile(pendingFile)">
                                <v-icon small>mdi-close</v-icon>
                              </v-btn>
                            </v-list-item-action>
                          </v-list-item>

                          <v-list-item
                            class="new-asset-drop-zone-row d-flex align-center justify-center"
                            :class="{ 'new-asset-drop-zone-hover': dragOverFormatInstanceId === formatInstance.format_instance_id }"
                            @dragover.prevent="onFormatDragOver(formatInstance)"
                            @dragleave.prevent="onFormatDragLeave"
                            @drop.prevent="onFormatDrop($event, formatInstance)"
                          >
                            <v-icon small color="primary" class="mr-1">mdi-plus</v-icon>
                            <span class="text-caption primary--text font-weight-medium text-uppercase">{{ $t('new_file') }}</span>
                            <span class="text-caption grey--text ml-2">{{ $t('drop_files_here_to_assign') }}</span>
                          </v-list-item>
                        </v-list>
                      </div>
                    </v-card>
                  </div>
                </v-expand-transition>
              </v-card>
            </div>
          </v-card>
        </v-col>
      </v-row>


      <!-- Bottom Action Bar -->
      <v-row no-gutters class="footer-row mt-4">
        <v-col cols="12" class="d-flex align-center justify-end">
          <v-alert
            v-if="unassignedFiles.length"
            dense
            outlined
            type="warning"
            icon="mdi-alert-outline"
            class="mb-0 mr-4 py-2"
          >
            {{ $t('unassigned_files_warning', { count: unassignedFiles.length }) }}
          </v-alert>

          <v-spacer></v-spacer>

          <v-btn text class="mr-2" @click="clearAll" :disabled="!pendingFiles.length || uploading">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="uploading"
            :disabled="!canSubmit"
            @click="submitUpload"
          >
            <v-icon left>mdi-tray-arrow-up</v-icon>
            {{ $t('upload_assigned_files') }} ({{ assignedFiles.length }})
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <!-- Attach File Dialog: either browse local files, or select from Contents -->
    <v-dialog v-model="attachFileDialog" max-width="520px">
      <v-card>
        <v-card-title>{{ $t('attach_file') }}</v-card-title>
        <v-card-text>
          <v-file-input
            v-model="dialogFiles"
            multiple
            outlined
            dense
            hide-details
            prepend-icon="mdi-paperclip"
            :placeholder="$t('select_a_file')"
          ></v-file-input>
          <div class="text-caption grey--text text-right mt-1">
            {{ $t('files_count_total', { count: dialogFiles.length, size: formatFileSize(dialogFilesTotalSize) }) }}
          </div>

          <div class="d-flex align-center my-4 attach-file-divider">
            <v-divider></v-divider>
            <span class="mx-3 text-caption grey--text">{{ $t('or') }}</span>
            <v-divider></v-divider>
          </div>

          <v-btn outlined block color="primary" @click="openSelectFromContentsDialog">
            <v-icon left>mdi-folder-open-outline</v-icon>
            {{ $t('select_from_contents') }}
          </v-btn>

          <div v-if="dialogContentNodes.length" class="mt-3">
            <v-chip
              v-for="node in dialogContentNodes"
              :key="node.node_id"
              close
              small
              class="mr-2 mb-2"
              @click:close="removeDialogContentNode(node)"
            >
              {{ node.name }}
            </v-chip>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAttachFileDialog">{{ $t('cancel') }}</v-btn>
          <v-btn text color="primary" :disabled="!canConfirmAttachFileDialog" @click="confirmAttachFileDialog">{{ $t('send') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--
      SelectFileField is kept mounted (but visually hidden) so its own
      internal file browser dialog can be opened directly - via the ref -
      as soon as "select from contents" is clicked, skipping its
      intermediate "field" placeholder UI entirely.
    -->
    <SelectFileField
      ref="selectFileField"
      class="d-none"
      :label="$t('markup.select_file_from_contents')"
      :parent-node-id="contentRootNodeId"
      :multiple-select="true"
      @selectNodes="onContentFilesSelected"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import JobAPI from '@/services/api/job/job'
import JobDeliverableAPI from '@/services/api/job/deliverable'
import JobDeliverAssetAPI from '@/services/api/job/job_deliver_asset'
import AiAssetAssociationAPI, { resolveAssetAssociation } from '@/services/api/job/ai_asset_association'
import DeliverableAPI from '@/services/api/business/deliverable'
import SelectFileField from '@/components/content/default/Form/SelectFileField.vue'
import AppActions from '@/store/app/actions-types'
import ContentActions from '@/store/content/actions/actions-types'

let fileUidCounter = 0

export default {
  name: 'JobMultiFileUploadManagement',

  components: {
    SelectFileField
  },

  props: {
    jobId: {
      type: [String, Number],
      default: null
    },
    jobData: {
      type: Object,
      default: () => ({})
    },
    customerId: {
      type: [String, Number],
      default: null
    },
    contentAppId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      loading: false,
      uploading: false,
      associating: false,
      aiAssociationAvailable: false,
      localJobData: {},
      baseInstances: [],
      deliverableOptions: [],
      expandedDeliverables: {},
      expandedFormats: {},
      pendingFiles: [],
      selectedFileUids: [],
      statusFilter: 'unassigned',
      draggingFileUid: null,
      draggingFileUids: [],
      dragOverFormatInstanceId: null,
      dragOverAssetId: null,
      isDraggingNativeFile: false,
      attachFileDialog: false,
      dialogFiles: [],
      dialogContentNodes: []
    }
  },

  computed: {
    effectiveJobId() {
      return this.jobId || this.$route.params.jobId || null
    },

    dialogFilesTotalSize() {
      return this.dialogFiles.reduce((total, file) => total + (file.size || 0), 0)
    },

    canConfirmAttachFileDialog() {
      return !this.loading && !this.uploading && !this.associating &&
        (this.dialogFiles.length > 0 || this.dialogContentNodes.length > 0)
    },

    effectiveCustomerId() {
      return this.customerId || this.localJobData?.customer_id || this.jobData?.customer_id || null
    },

    contentRootNodeId() {
      return (
        this.localJobData?.job_folder_id ||
        this.localJobData?.ref_folder_id ||
        this.localJobData?.mat_folder_id ||
        null
      )
    },

    totalFormats() {
      return this.baseInstances.reduce((sum, base) => sum + (base.formatInstances || []).length, 0)
    },

    unassignedFiles() {
      return this.pendingFiles.filter(file => !file.formatInstanceId)
    },

    assignedFiles() {
      return this.pendingFiles.filter(file => !!file.formatInstanceId)
    },

    filteredFiles() {
      if (this.statusFilter === 'unassigned') return this.unassignedFiles
      if (this.statusFilter === 'assigned') return this.assignedFiles
      return this.pendingFiles
    },

    canSubmit() {
      return !this.uploading && !this.associating && this.assignedFiles.length > 0 && this.unassignedFiles.length === 0
    },

    allDeliverablesExpanded() {
      return this.baseInstances.length > 0 &&
        this.baseInstances.every(base => this.expandedDeliverables[base.base_instance_id])
    }
  },

  watch: {
    'localJobData.job_definition_id': {
      immediate: true,
      handler() {
        this.loadAiAssociationAvailability()
      }
    },
    jobData: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.localJobData = { ...this.localJobData, ...newVal }
        }
      }
    }
  },

  mounted() {
    this.initialize()
  },

  methods: {
    async loadAiAssociationAvailability() {
      this.aiAssociationAvailable = false
      const definitionId = this.localJobData.job_definition_id
      if (!definitionId) return
      try {
        const enabler = await AiAssetAssociationAPI.getEnabler(this.localJobData)
        const rawParams = enabler?.params ?? enabler?.enabler_params
        const params = typeof rawParams === 'string' ? JSON.parse(rawParams) : rawParams
        if (this.localJobData.job_definition_id === definitionId) {
          this.aiAssociationAvailable = typeof params?.n8n_url === 'string' && !!params.n8n_url.trim()
        }
      } catch (error) {
        console.error('[AI asset association] Enabler check failed:', error.message)
      }
    },

    async associateWithAi() {
      if (!this.aiAssociationAvailable || this.loading || this.uploading || this.associating) return
      await this.suggestFileAssociations([...this.unassignedFiles])
    },

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),
    ...mapActions('content/actions', [
      ContentActions.UPLOAD_FILE
    ]),

    async initialize() {
      this.loading = true

      try {
        if (!this.localJobData?.job_id) {
          await this.loadJobData()
        }

        await this.loadDeliverables()

        if (this.effectiveJobId) {
          await this.loadBaseInstances()
          this.expandAllDeliverables()
        }
      } finally {
        this.loading = false
      }
    },

    async loadJobData() {
      if (!this.effectiveJobId) return

      try {
        const response = await JobAPI.getJob(this.effectiveJobId)
        this.localJobData = { ...this.localJobData, ...response.data }
      } catch (error) {
        console.error('Error loading job data:', error)
      }
    },

    async loadDeliverables() {
      if (!this.effectiveCustomerId) return

      try {
        const response = await DeliverableAPI.getAllDeliverablesByCustomer(this.effectiveCustomerId, {
          order: 'deliverable_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.deliverableOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading deliverables:', error)
      }
    },

    async loadBaseInstances() {
      try {
        const response = await JobDeliverableAPI.getJobDeliverables(this.effectiveJobId)
        const deliverables = response.data || []

        this.baseInstances = await Promise.all(deliverables.map(async item => {
          const baseInstance = { ...item.base_instance }

          baseInstance.formatInstances = await Promise.all((item.format_instances || []).map(async formatItem => {
            const formatInstance = { ...formatItem.format_instance }

            if (formatInstance.format !== undefined) {
              formatInstance.format_desc = formatInstance.format
              delete formatInstance.format
            }

            formatInstance.job_deliver = formatItem.job_deliver || null
            formatInstance.assets = await this.loadFormatAssets(formatInstance)

            return formatInstance
          }))

          return baseInstance
        }))

        this.baseInstances.forEach(baseInstance => {
          (baseInstance.formatInstances || []).forEach(formatInstance => {
            this.$set(this.expandedFormats, formatInstance.format_instance_id, true)
          })
        })
      } catch (error) {
        console.error('Error loading base instances:', error)
      }
    },

    async loadFormatAssets(formatInstance) {
      const jobDeliverId = formatInstance?.job_deliver?.job_deliver_id

      if (!jobDeliverId || !this.effectiveJobId) {
        return []
      }

      try {
        const response = await JobDeliverAssetAPI.readAssets(jobDeliverId, this.effectiveJobId)
        const jobDeliverAssets = Array.isArray(response.data) ? response.data : []

        return jobDeliverAssets.map(assetData => {
          const markupSession = assetData.markup_session

          if (!markupSession) return null

          const markupSessionNodes = markupSession.markup_session_node || []
          const latestNode = markupSessionNodes.reduce((latest, current) => {
            if (!latest || current.version > latest.version) return current
            return latest
          }, null)

          return {
            job_deliver_asset_id: assetData.job_deliver_asset_id,
            job_deliver_id: jobDeliverId,
            markup_session_id: markupSession.markup_session_id || markupSession.id,
            name: markupSession.name || this.$t('markup.unnamed_asset'),
            fileName: latestNode?.file_info?.name || latestNode?.name || '',
            currentVersion: latestNode ? latestNode.version : 1
          }
        }).filter(Boolean)
      } catch (error) {
        console.error('Error loading format assets:', error)
        return []
      }
    },

    getFormatAssets(formatInstance) {
      return formatInstance.assets || []
    },

    getFormatFullName(baseInstance, formatInstance) {
      return `${baseInstance.base_title || ''}-${formatInstance.format_desc || ''}`.trim()
    },

    toggleDeliverable(baseInstance) {
      const id = baseInstance.base_instance_id
      this.$set(this.expandedDeliverables, id, !this.expandedDeliverables[id])
    },

    expandAllDeliverables() {
      this.baseInstances.forEach(base => {
        this.$set(this.expandedDeliverables, base.base_instance_id, true)
        ;(base.formatInstances || []).forEach(formatInstance => {
          this.$set(this.expandedFormats, formatInstance.format_instance_id, true)
        })
      })
    },

    toggleAll() {
      const expand = !this.allDeliverablesExpanded

      this.baseInstances.forEach(base => {
        this.$set(this.expandedDeliverables, base.base_instance_id, expand)

        if (expand) {
          (base.formatInstances || []).forEach(formatInstance => {
            this.$set(this.expandedFormats, formatInstance.format_instance_id, true)
          })
        }
      })
    },

    toggleFormat(formatInstance) {
      const id = formatInstance.format_instance_id
      this.$set(this.expandedFormats, id, !this.expandedFormats[id])
    },

    isFormatExpanded(formatInstance) {
      const id = formatInstance.format_instance_id
      return this.expandedFormats[id] !== false
    },

    // ---------- File handling (left column) ----------

    onNativeDragOver() {
      this.isDraggingNativeFile = true
    },

    onNativeDragLeave() {
      this.isDraggingNativeFile = false
    },

    onNativeDrop(event) {
      this.isDraggingNativeFile = false
      const files = Array.from(event.dataTransfer?.files || [])
      this.addFiles(files)
    },

    addFiles(files) {
      files.forEach(file => {
        this.pendingFiles.push({
          uid: `pf-${++fileUidCounter}`,
          file,
          name: file.name,
          size: file.size,
          formatInstanceId: null,
          assignedAssetId: null,
          fromContent: false,
          contentNode: null
        })
      })
    },

    // ---------- Attach File dialog (paperclip + select from contents) ----------

    openAttachFileDialog() {
      this.dialogFiles = []
      this.dialogContentNodes = []
      this.attachFileDialog = true
    },

    closeAttachFileDialog() {
      this.attachFileDialog = false
      this.dialogFiles = []
      this.dialogContentNodes = []
    },

    confirmAttachFileDialog() {
      if (this.associating || !this.canConfirmAttachFileDialog) return
      this.addFiles(this.dialogFiles)

      this.dialogContentNodes.forEach(node => {
        this.pendingFiles.push({
          uid: `pf-${++fileUidCounter}`,
          file: null,
          name: node.name,
          size: node.size || 0,
          formatInstanceId: null,
          assignedAssetId: null,
          fromContent: true,
          contentNode: node
        })
      })

      this.closeAttachFileDialog()
    },

    async suggestFileAssociations(files) {
      if (!files.length) return
      this.associating = true
      const jobId = this.effectiveJobId
      try {
        const suggestions = await AiAssetAssociationAPI.suggest({
          ...this.localJobData,
          job_id: jobId
        }, files.map(file => file.name))
        if (this.effectiveJobId !== jobId) {
          return
        }


        files.forEach(file => {
          if (!this.pendingFiles.includes(file) || file.formatInstanceId || file.assignedAssetId) {
            return
          }
          const matches = suggestions.filter(item => item && item.file === file.name)
          if (matches.length !== 1) {
            return
          }
          const association = resolveAssetAssociation(matches[0], this.baseInstances)
          if (association) {
            Object.assign(file, association)
          }
        })
        this.expandAllDeliverables()
      } catch (error) {
        console.error('[AI asset association] Batch failed:', JSON.stringify({
          message: error.message, status: error.response?.status,
          response: error.response?.data, error: error.error, stack: error.stack
        }))
        this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('ai_asset_association_failed'))
      } finally {
        this.associating = false
      }
    },

    openSelectFromContentsDialog() {
      // Bypasses SelectFileField's own "field" placeholder click handler and
      // opens its internal file browser dialog directly.
      this.$refs.selectFileField.selectFile()
    },

    onContentFilesSelected(nodes) {
      const nodesArray = nodes || []
      const existingIds = this.dialogContentNodes.map(node => node.node_id)

      nodesArray.forEach(node => {
        if (!existingIds.includes(node.node_id)) {
          this.dialogContentNodes.push(node)
        }
      })
    },

    removeDialogContentNode(node) {
      this.dialogContentNodes = this.dialogContentNodes.filter(n => n.node_id !== node.node_id)
    },

    // Applies the appropriate action for the whole selection when it
    // includes the given file, otherwise applies it to just that file.
    handleFileAction(pendingFile) {
      const files = this.isFileSelected(pendingFile)
        ? this.pendingFiles.filter(pf => this.isFileSelected(pf))
        : [pendingFile]

      files.forEach(file => {
        if (file.formatInstanceId) {
          this.unassignFile(file)
        } else {
          this.removeFile(file)
        }
      })
    },

    removeFile(pendingFile) {
      this.pendingFiles = this.pendingFiles.filter(pf => pf.uid !== pendingFile.uid)
      this.selectedFileUids = this.selectedFileUids.filter(uid => uid !== pendingFile.uid)
    },

    clearAll() {
      this.pendingFiles = []
      this.selectedFileUids = []
    },

    unassignFile(pendingFile) {
      pendingFile.formatInstanceId = null
      pendingFile.assignedAssetId = null
    },

    // ---------- Multi-selection ----------

    isFileSelected(pendingFile) {
      return this.selectedFileUids.includes(pendingFile.uid)
    },

    toggleFileSelection(pendingFile) {
      if (this.isFileSelected(pendingFile)) {
        this.selectedFileUids = this.selectedFileUids.filter(uid => uid !== pendingFile.uid)
      } else {
        this.selectedFileUids = [...this.selectedFileUids, pendingFile.uid]
      }
    },

    clearSelection() {
      this.selectedFileUids = []
    },


    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let value = bytes
      let unitIndex = 0

      while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024
        unitIndex++
      }

      return `${value.toFixed(1)} ${units[unitIndex]}`
    },

    fileExtension(pendingFile) {
      const parts = pendingFile.name.split('.')
      return parts.length > 1 ? parts.pop().toUpperCase() : ''
    },

    fileIcon(pendingFile) {
      const ext = this.fileExtension(pendingFile).toLowerCase()
      if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return 'mdi-file-video-outline'
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'mdi-file-image-outline'
      if (['pdf'].includes(ext)) return 'mdi-file-pdf-box'
      return 'mdi-file-outline'
    },

    getAssignedFormatLabel(pendingFile) {
      for (const base of this.baseInstances) {
        const format = (base.formatInstances || []).find(f => f.format_instance_id === pendingFile.formatInstanceId)
        if (format) {
          if (pendingFile.assignedAssetId) {
            const asset = (format.assets || []).find(a => a.job_deliver_asset_id === pendingFile.assignedAssetId)
            if (asset) {
              return `${base.base_title} • ${format.format_desc} • ${asset.name} (${this.$t('markup.new_version')})`
            }
          }
          return `${base.base_title} • ${format.format_desc}`
        }
      }
      return ''
    },

    getAssignedFilesForFormat(formatInstance) {
      return this.pendingFiles.filter(pf => pf.formatInstanceId === formatInstance.format_instance_id && !pf.assignedAssetId)
    },

    getAssignedVersionFilesForAsset(asset) {
      return this.pendingFiles.filter(pf => pf.assignedAssetId === asset.job_deliver_asset_id)
    },

    // The most recently assigned pending file for this asset "overwrites"
    // the asset row itself (name/version shown inline instead of a nested row).
    getLatestPendingVersionForAsset(asset) {
      const filesForAsset = this.getAssignedVersionFilesForAsset(asset)
      return filesForAsset.length ? filesForAsset[filesForAsset.length - 1] : null
    },

    // The file name shown under the asset name: the newly assigned pending
    // file (if any) takes precedence, otherwise the current/last uploaded
    // file for that asset.
    getAssetCurrentFileName(asset) {
      const latestPendingVersion = this.getLatestPendingVersionForAsset(asset)
      if (latestPendingVersion) {
        return latestPendingVersion.name
      }

      return asset.fileName || this.$t('markup.unnamed_asset')
    },

    // Any further pending files for the same asset beyond the latest one are
    // still rendered as additional nested rows (further queued versions).
    getExtraAssignedVersionFilesForAsset(asset) {
      const filesForAsset = this.getAssignedVersionFilesForAsset(asset)
      return filesForAsset.slice(0, -1)
    },

    getNextVersionForAsset(asset, pendingFile) {
      // Pending files assigned to the same asset are versioned sequentially,
      // in the order they were assigned (insertion order in pendingFiles).
      const filesForAsset = this.getAssignedVersionFilesForAsset(asset)
      const positionIndex = filesForAsset.findIndex(pf => pf.uid === pendingFile.uid)
      return asset.currentVersion + positionIndex + 1
    },

    // ---------- Drag and Drop between columns ----------

    onFileDragStart(event, pendingFile) {
      // Dragging a file that's part of the current selection drags the
      // whole selection along with it; otherwise only that single file.
      this.draggingFileUids = this.isFileSelected(pendingFile)
        ? [...this.selectedFileUids]
        : [pendingFile.uid]
      this.draggingFileUid = pendingFile.uid

      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', JSON.stringify(this.draggingFileUids))
    },

    onFileDragEnd() {
      this.draggingFileUid = null
      this.draggingFileUids = []
      this.dragOverFormatInstanceId = null
      this.dragOverAssetId = null
    },

    getDraggedFileUids(event) {
      const raw = event.dataTransfer.getData('text/plain')

      if (raw) {
        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) return parsed
        } catch (error) {
          // Not JSON (e.g. plain uid from an older drag) - fall back below.
          return [raw]
        }
      }

      return this.draggingFileUids.length ? this.draggingFileUids : (this.draggingFileUid ? [this.draggingFileUid] : [])
    },

    onAssetDragOver(asset) {
      this.dragOverAssetId = asset.job_deliver_asset_id
    },

    onAssetDragLeave() {
      this.dragOverAssetId = null
    },

    // Dropping onto an existing asset creates a new version of that asset.
    // This only ever applies to a single file, even if multiple files are
    // selected/dragged - only the file actually dropped is used.
    onAssetDrop(event, formatInstance, asset) {
      const uid = this.draggingFileUid || this.getDraggedFileUids(event)[0]
      const pendingFile = this.pendingFiles.find(pf => pf.uid === uid)

      if (pendingFile) {
        pendingFile.formatInstanceId = formatInstance.format_instance_id
        pendingFile.assignedAssetId = asset.job_deliver_asset_id
      }

      this.dragOverAssetId = null
      this.dragOverFormatInstanceId = null
      this.draggingFileUid = null
      this.draggingFileUids = []
    },

    onFormatDragOver(formatInstance) {
      this.dragOverFormatInstanceId = formatInstance.format_instance_id
    },

    onFormatDragLeave() {
      this.dragOverFormatInstanceId = null
    },

    // Dropping onto a format's "new asset" zone assigns all dragged files
    // (the whole selection, when applicable) as new assets for that format.
    onFormatDrop(event, formatInstance) {
      const uids = this.getDraggedFileUids(event)

      this.pendingFiles
        .filter(pf => uids.includes(pf.uid))
        .forEach(pendingFile => {
          pendingFile.formatInstanceId = formatInstance.format_instance_id
          pendingFile.assignedAssetId = null
        })

      this.dragOverFormatInstanceId = null
      this.draggingFileUid = null
      this.draggingFileUids = []
    },


    // ---------- Upload / Submit ----------

    async submitUpload() {
      if (!this.canSubmit) return

      this.uploading = true

      try {
        const concurrencyCount = parseInt(process.env.VUE_APP_UPLOAD_CONCURRENCY) || 3
        const filesToProcess = [...this.assignedFiles]

        const batches = Array.from(
          { length: Math.min(concurrencyCount, filesToProcess.length) },
          () => []
        )

        filesToProcess.forEach((file, index) => {
          batches[index % batches.length].push(file)
        })

        // Each entry in resultsByUid ends up shaped exactly as the backend
        // expects: { node_id, job_deliver_id, markup_session_id, version }.
        // node_id is only known after the underlying file has been uploaded;
        // until then the pending file is tracked by its own uid/name.
        const resultsByUid = {}

        await Promise.all(
          batches.map(async batch => {
            for (const pendingFile of batch) {
              try {
                resultsByUid[pendingFile.uid] = await this.buildAssetPayloadForFile(pendingFile)
                pendingFile.uploaded = true
              } catch (error) {
                console.error(`Error uploading file "${pendingFile.name}":`, error)
                pendingFile.uploadError = true
              }
            }
          })
        )

        const assetsPayload = Object.values(resultsByUid).filter(Boolean)

        if (assetsPayload.length) {
          await JobDeliverAssetAPI.associateAssets(this.effectiveJobId, assetsPayload)
        }

        this.pendingFiles = this.pendingFiles.filter(pf => !pf.uploaded)

        await this.loadBaseInstances()

        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('markup.asset_uploaded_successfully'))
      } catch (error) {
        console.error('Error uploading files:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('markup.error_uploading_asset'))
      } finally {
        this.uploading = false
      }
    },

    findFormatInstance(formatInstanceId) {
      for (const base of this.baseInstances) {
        const format = (base.formatInstances || []).find(f => f.format_instance_id === formatInstanceId)
        if (format) return format
      }
      return null
    },

    findAssignedAsset(formatInstance, assignedAssetId) {
      if (!formatInstance || !assignedAssetId) return null
      return (formatInstance.assets || []).find(a => a.job_deliver_asset_id === assignedAssetId) || null
    },

    /**
     * Uploads (or resolves, for content-selected files) the physical file and
     * returns the payload item the backend expects for this asset:
     * { node_id, job_deliver_id, markup_session_id, version }.
     *
     * - Dropped onto an existing asset -> new version of that markup session.
     * - Dropped onto a format's "new asset" zone -> brand new asset
     *   (physical uploads reuse the session created by prepareStructure;
     *   content-selected files let associateAssets create the session).
     */
    async buildAssetPayloadForFile(pendingFile) {
      const formatInstance = this.findFormatInstance(pendingFile.formatInstanceId)

      if (!formatInstance) return null

      const jobDeliverId = formatInstance?.job_deliver?.job_deliver_id || null
      const assignedAsset = this.findAssignedAsset(formatInstance, pendingFile.assignedAssetId)

      const markupSessionId = assignedAsset ? assignedAsset.markup_session_id : null
      const version = assignedAsset
        ? this.getNextVersionForAsset(assignedAsset, pendingFile)
        : null

      // File already lives in Contents - no physical upload needed, just
      // reference its existing node_id.
      if (pendingFile.fromContent && pendingFile.contentNode) {
        return {
          node_id: pendingFile.contentNode.node_id,
          job_deliver_id: jobDeliverId,
          markup_session_id: markupSessionId,
          version
        }
      }

      const assetName = pendingFile.name.replace(/\.[^/.]+$/, '')

      const prepareResponse = await JobDeliverAssetAPI.prepareStructure({
        job_id: this.effectiveJobId,
        format_instance_id: formatInstance.format_instance_id,
        markup_session_name: assignedAsset ? undefined : assetName,
        markup_session_id: markupSessionId
      })

      const { node_id: nodeId, markup_session_id: preparedMarkupSessionId } = prepareResponse.data

      const uploadResult = await this[ContentActions.UPLOAD_FILE]({
        parentId: nodeId,
        file: pendingFile.file,
        fieldValues: {
          no_auto_link_job: true
        }
      })

      return {
        node_id: uploadResult.nodeId,
        job_deliver_id: jobDeliverId,
        markup_session_id: preparedMarkupSessionId,
        version
      }
    },

    getBaseInstanceIcon(baseInstance) {
      const deliverable = this.deliverableOptions.find(d => d.deliverable_id === baseInstance.deliverable_id)
      return deliverable?.icon || 'mdi-package-variant'
    },
  }
}
</script>

<style scoped lang="scss">
.job-multi-file-upload-management {
  height: 100%;
  min-height: 0;

  // Relying only on `flex-grow-1` to size `.container`/`.upload-columns`
  // is not enough: some browsers won't treat a flex-grown (as opposed to
  // an explicitly-sized) box as a "definite" height, so descendants using
  // `height: 100%` further down the chain can resolve to `auto` and grow
  // with their content instead of being capped. Pin an explicit
  // `height: 100%` at every level in addition to the flex-grow so the
  // percentage chain always has something definite to resolve against.
  > .container {
    height: 100%;
    min-height: 0;
  }
}

.upload-columns {
  align-items: stretch;
  height: 100%;
  min-height: 0;

  // v-col renders as `.col-12.col-md-5` (etc.) here, not a plain `.col`,
  // since both `cols` and a breakpoint prop are set - target it via the
  // attribute selector so the flex min-height chain isn't broken.
  > [class*='col-'] {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
}

// Vuetify's own `.row` class sets `flex: 1 1 auto` on every v-row (see the
// `make-row` mixin), so without this override the bottom action bar row
// would compete with `.upload-columns` for the container's remaining
// height instead of sitting at its natural (fixed) content height.
.footer-row {
  flex: 0 0 auto;
}

.holding-card,
.deliverables-card {
  background-color: #fff;
  width: 100%;
  height: 100%;
  // Without this, the card (itself a flex item of its .col) defaults to
  // min-height: auto and grows to fit its content once it's loaded (e.g.
  // many deliverables/formats), overflowing past its fixed height instead
  // of letting its internal *-scroll area handle the overflow.
  min-height: 0;
  border: 2px dashed transparent;
  transition: border-color 0.2s, background-color 0.2s;

  &.is-dragging-native {
    border-color: var(--v-primary-base);
    background-color: color-mix(in srgb, var(--v-primary-base) 8%, transparent);
  }
}

.holding-card-scroll,
.deliverables-card-scroll {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.empty-holding-state {
  min-height: 0;
}

.empty-holding-state-hint {
  max-width: 320px;
}

.associate-with-ai-button.v-btn {
  background: linear-gradient(to right, #2e7d32, #1565c0) !important;
  color: #fff !important;

  &.v-btn--disabled {
    background: #e0e0e0 !important;
    color: #757575 !important;
  }
}

.native-drop-zone {
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  display: flex;
  padding: 12px;
  justify-content: center;
  color: #757575;
  transition: border-color 0.2s, background-color 0.2s;

  &.is-dragging-native {
    border-color: var(--v-primary-base);
    background-color: color-mix(in srgb, var(--v-primary-base) 8%, transparent);
  }
}

.file-card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-card {
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-left: 4px solid #ff9800;
  border-radius: 8px;
  padding: 10px 12px;
  background-color: #fff;
  cursor: grab;
  transition: box-shadow 0.15s, opacity 0.15s;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  &.file-card-assigned {
    border-left-color: #4caf50;
  }

  &.file-card-dragging {
    opacity: 0.4;
  }

  &.file-card-selected {
    background-color: color-mix(in srgb, var(--v-primary-base) 8%, transparent);
    border-color: var(--v-primary-base);
  }
}

.file-card-actions {
  flex: 0 0 auto;
}

.file-card-checkbox {
  ::v-deep .v-input--selection-controls__input {
    margin-right: 0;
  }
}

.drag-handle {
  cursor: grab;
}

.deliverable-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f4f5f9;
}

.format-drop-card {
  background-color: #eaebef !important;
  border-left: 4px solid var(--v-primary-base);
  border-radius: 10px;
  transition: background-color 0.2s;

  &.format-drop-card-hover {
    border-color: var(--v-primary-base);
    // NOTE: --v-primary-base holds a full color value (e.g. "#4cab3d"), not
    // comma-separated R,G,B channels, so rgba(var(--v-primary-base), n) is
    // invalid CSS and gets silently dropped by the browser. color-mix() lets
    // us blend the theme primary color with transparent at a given
    // percentage, keeping true translucency while still using the CSS var.
    // !important is needed here to win over Vuetify's own theme background
    // classes applied on the underlying v-sheet/v-card (same CSS specificity,
    // but loaded after this component's scoped styles).
    background-color: color-mix(in srgb, var(--v-primary-base) 8%, transparent) !important;
  }
}

.existing-asset-list {
  display: flex;
  flex-direction: column;
  border: 1px solid #cfcfcf;
  border-radius: 6px;
  overflow: visible;
}

.existing-asset-row {
  padding: 8px 12px;
  border: 2px dashed transparent;
  position: relative;
  z-index: 0;
  transition: border-color 0.15s, background-color 0.15s;

  & + .existing-asset-row {
    border-top: 1px solid #eee;
  }

  &.existing-asset-row-hover {
    // Fully override the border shorthand (not just border-color), since the
    // sibling-combinator rule above narrows border-top to "1px solid #eee",
    // which would otherwise stay thinner than the dashed hover border and
    // make the top edge look clipped.
    border: 2px dashed var(--v-primary-base);
    background-color: color-mix(in srgb, var(--v-primary-base) 8%, transparent);
    position: relative;
    z-index: 1;
  }
}

.new-asset-drop-zone-row {
  padding: 10px 12px;
  border: 2px dashed transparent;
  border-top: 1px solid #eee;
  position: relative;
  z-index: 0;
  transition: border-color 0.2s, background-color 0.2s;

  &.new-asset-drop-zone-hover {
    // Same as above: override the full border shorthand so the permanent
    // top separator doesn't shrink the top edge of the dashed hover border.
    border: 2px dashed var(--v-primary-base);
    background-color: color-mix(in srgb, var(--v-primary-base) 8%, transparent);
    position: relative;
    z-index: 1;
  }
}
</style>
